import React, { useState, useEffect, useRef } from 'react';

const API = process.env.NODE_ENV === 'production' ? '' : 'http://localhost:8000';
const ADMIN_KEY = 'jira-dashboard-admin';

function fmt(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('fr-BE', {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit', timeZone: 'Europe/Brussels'
  });
}

function isSync(filename) { return filename?.endsWith('.sync'); }

function Section({ title, children }) {
  return (
    <div className="card" style={{ marginBottom: 20 }}>
      <div className="settings-section-title">{title}</div>
      {children}
    </div>
  );
}

export default function SettingsPage({ onUploaded }) {
  const [uploads, setUploads]       = useState([]);
  const [syncing, setSyncing]       = useState(false);
  const [backfilling, setBackfilling] = useState(false);
  const [backfillMsg, setBackfillMsg] = useState(null);
  const [uploading, setUploading]   = useState(false);
  const [jiraOk, setJiraOk]         = useState(null);
  const [jiraUser, setJiraUser]     = useState('');
  const [error, setError]           = useState(null);
  const [success, setSuccess]       = useState(null);
  const [dragover, setDragover]     = useState(false);
  const [cookie, setCookie]         = useState('');
  const [jiraUrl, setJiraUrl]       = useState('');
  const [savedJiraUrl, setSavedJiraUrl] = useState('');

  // Admin credentials — remembered in localStorage
  const [adminUser, setAdminUser]   = useState(() => {
    try { return JSON.parse(localStorage.getItem(ADMIN_KEY) || '{}').user || ''; } catch { return ''; }
  });
  const [adminPass, setAdminPass]   = useState(() => {
    try { return JSON.parse(localStorage.getItem(ADMIN_KEY) || '{}').pass || ''; } catch { return ''; }
  });
  const [rememberAdmin, setRememberAdmin] = useState(() => {
    try { return !!JSON.parse(localStorage.getItem(ADMIN_KEY) || '{}').user; } catch { return false; }
  });

  const [cookieSaving, setCookieSaving] = useState(false);
  const [urlSaving, setUrlSaving]   = useState(false);
  const [sso, setSso]               = useState({ enabled: false, tenant_id: '', client_id: '', client_secret: '', allowed_emails: '' });
  const [ssoSaving, setSsoSaving]   = useState(false);
  const [ssoMsg, setSsoMsg]         = useState('');
  const [bmToken, setBmToken]       = useState(null);
  const [bmLoading, setBmLoading]   = useState(false);
  const fileRef = useRef();

  const saveAdminToStorage = (user, pass) => {
    if (rememberAdmin) localStorage.setItem(ADMIN_KEY, JSON.stringify({ user, pass }));
    else localStorage.removeItem(ADMIN_KEY);
  };

  const authHeader = () => ({ 'Authorization': 'Basic ' + btoa(unescape(encodeURIComponent(adminUser + ':' + adminPass))) });

  const loadUploads = () => {
    fetch(`${API}/api/uploads`).then(r => r.json()).then(setUploads).catch(() => {});
  };

  const checkJira = () => {
    fetch(`${API}/api/setup/cookie/status`)
      .then(r => r.json())
      .then(d => { setJiraOk(d.ok); setJiraUser(d.display_name || ''); })
      .catch(() => setJiraOk(false));
  };

  const generateBookmarklet = async () => {
    if (!adminUser || !adminPass) { setError('Enter your dashboard admin credentials first'); return; }
    setBmLoading(true); setError(null);
    saveAdminToStorage(adminUser, adminPass);
    try {
      const r = await fetch(`${API}/api/jira/bookmarklet-token/generate`, {
        method: 'POST', headers: { ...authHeader() },
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.detail || 'Failed to generate token');
      setBmToken(data.token);
    } catch(e) { setError(e.message); }
    setBmLoading(false);
  };

  const loadSettings = () => {
    fetch(`${API}/api/settings`)
      .then(r => r.json())
      .then(d => { setSavedJiraUrl(d.jira_url || ''); setJiraUrl(d.jira_url || ''); })
      .catch(() => {});
  };

  useEffect(() => {
    loadUploads(); checkJira(); loadSettings();
    fetch(`${API}/api/settings/sso`).then(r => r.json()).then(d => setSso({
      ...d,
      allowed_emails: (d.allowed_emails || '').split(',').map(e => e.trim()).filter(Boolean).join('\n'),
    })).catch(() => {});
  }, []);

  const doSync = async () => {
    setSyncing(true); setError(null); setSuccess(null);
    try {
      const r = await fetch(`${API}/api/jira/sync`, { method: 'POST' });
      const data = await r.json();
      if (!r.ok) throw new Error(data.detail || 'Sync failed');
      setSuccess(`✓ Synced ${data.ticket_count} tickets from Jira`);
      loadUploads(); onUploaded();
    } catch(e) { setError(e.message); }
    setSyncing(false);
  };

  const doBackfill = async () => {
    setBackfilling(true); setBackfillMsg(null); setError(null);
    try {
      const r = await fetch(`${API}/api/jira/backfill`, { method: 'POST' });
      const data = await r.json();
      if (!r.ok) throw new Error(data.detail || 'Backfill failed to start');
      setBackfillMsg({ type: 'info', text: `Backfill started (${data.from_date} → ${data.to_date}) — running in background…` });
      // Poll for completion
      const poll = setInterval(async () => {
        try {
          const s = await fetch(`${API}/api/jira/backfill/status`).then(r => r.json());
          if (s.error) { clearInterval(poll); setBackfilling(false); setBackfillMsg({ type: 'error', text: `Backfill error: ${s.error}` }); }
          else if (s.done) { clearInterval(poll); setBackfilling(false); setBackfillMsg({ type: 'ok', text: `✓ Backfill complete — ${s.count} tickets imported` }); loadUploads(); onUploaded(); }
        } catch { clearInterval(poll); setBackfilling(false); }
      }, 3000);
    } catch(e) { setError(e.message); setBackfilling(false); }
  };

  const saveCookie = async () => {
    if (!cookie.trim()) { setError('Please paste your JSESSIONID value'); return; }
    if (!adminUser || !adminPass) { setError('Enter your dashboard admin credentials'); return; }
    setCookieSaving(true); setError(null); setSuccess(null);
    saveAdminToStorage(adminUser, adminPass);
    try {
      const r = await fetch(`${API}/api/setup/cookie`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeader() },
        body: JSON.stringify({ jsessionid: cookie.trim(), jira_url: jiraUrl }),
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.detail || 'Failed to save cookie');
      setSuccess(`✓ Session active — connected as ${data.jira_display}`);
      setJiraOk(true); setJiraUser(data.jira_display);
      setCookie('');
    } catch(e) { setError(e.message); }
    setCookieSaving(false);
  };

  const saveJiraUrl = async () => {
    if (!adminUser || !adminPass) { setError('Enter admin credentials to save settings'); return; }
    setUrlSaving(true); setError(null);
    saveAdminToStorage(adminUser, adminPass);
    try {
      const r = await fetch(`${API}/api/settings`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ jira_url: jiraUrl, admin_user: adminUser, admin_pass: adminPass }),
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.detail || 'Failed to save');
      setSavedJiraUrl(jiraUrl);
      setSuccess('✓ Jira URL saved — links will now open in Jira automatically');
    } catch(e) { setError(e.message); }
    setUrlSaving(false);
  };

  const doUpload = async (file) => {
    if (!file || !file.name.endsWith('.csv')) { setError('Please select a CSV file.'); return; }
    setUploading(true); setError(null); setSuccess(null);
    const form = new FormData();
    form.append('file', file);
    try {
      const r = await fetch(`${API}/api/upload`, { method: 'POST', body: form });
      const data = await r.json();
      if (!r.ok) throw new Error(data.detail || 'Upload failed');
      setSuccess(`✓ Uploaded ${data.filename} — ${data.ticket_count} tickets imported.`);
      loadUploads(); onUploaded();
    } catch(e) { setError(e.message); }
    setUploading(false);
  };

  const labelStyle = { fontSize: 11, fontWeight: 600, color: 'var(--text-2)', display: 'block', marginBottom: 5 };

  return (
    <div style={{ maxWidth: 680, margin: '0 auto' }}>

      {/* Status messages */}
      {success && <div style={{ marginBottom: 16, padding: '12px 16px', background: 'var(--ok-light)', borderRadius: 8, color: 'var(--ok)', fontSize: 13 }}>{success}</div>}
      {error && <div style={{ marginBottom: 16, padding: '12px 16px', background: 'var(--critical-light)', borderRadius: 8, color: 'var(--critical)', fontSize: 13 }}>{error}</div>}

      {/* ── Admin Credentials ── */}
      <Section title="🔑 Admin Credentials">
        <p style={{ fontSize: 12, color: 'var(--text-2)', marginBottom: 14 }}>
          Required to save changes. Check "Remember me" to avoid re-entering every time.
        </p>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 10 }}>
          <div>
            <label style={labelStyle}>Username</label>
            <input type="text" className="input" value={adminUser} onChange={e => setAdminUser(e.target.value)} placeholder="admin" autoComplete="username" />
          </div>
          <div>
            <label style={labelStyle}>Password</label>
            <input type="password" className="input" value={adminPass} onChange={e => setAdminPass(e.target.value)} placeholder="••••••••" autoComplete="current-password" />
          </div>
        </div>
        <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: 'var(--text-2)', cursor: 'pointer' }}>
          <input type="checkbox" checked={rememberAdmin} onChange={e => {
            setRememberAdmin(e.target.checked);
            if (!e.target.checked) localStorage.removeItem(ADMIN_KEY);
            else saveAdminToStorage(adminUser, adminPass);
          }} />
          Remember credentials in this browser
        </label>
      </Section>

      {/* ── Jira Session ── */}
      <Section title={
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <span>🔗 Jira Connection</span>
          <span style={{ fontSize: 12, fontWeight: 400 }}>
            {jiraOk === null && <span style={{ color: 'var(--text-2)' }}>Checking…</span>}
            {jiraOk === true && <span style={{ color: 'var(--ok)', fontWeight: 600 }}>✓ Active — {jiraUser}</span>}
            {jiraOk === false && <span style={{ color: 'var(--critical)', fontWeight: 600 }}>✗ Session expired</span>}
          </span>
        </div>
      }>
        {/* Jira URL */}
        <div style={{ marginBottom: 16 }}>
          <label style={labelStyle}>Jira Base URL <span style={{ color: 'var(--ok)', fontWeight: 400 }}>(used for ticket links everywhere)</span></label>
          <div style={{ display: 'flex', gap: 8 }}>
            <input type="text" className="input mono" style={{ fontSize: 12 }} value={jiraUrl}
              onChange={e => setJiraUrl(e.target.value)} placeholder="https://infra.bewan.be" />
            <button className="btn btn-secondary" onClick={saveJiraUrl} disabled={urlSaving || !jiraUrl} style={{ whiteSpace: 'nowrap', flexShrink: 0 }}>
              {urlSaving ? 'Saving…' : 'Save URL'}
            </button>
          </div>
          {savedJiraUrl && <div style={{ fontSize: 11, color: 'var(--ok)', marginTop: 5 }}>✓ Saved: {savedJiraUrl}</div>}
        </div>

        {/* Cookie */}
        <div style={{ background: 'var(--accent-light)', borderRadius: 8, padding: '10px 14px', marginBottom: 14, fontSize: 12, lineHeight: 1.8 }}>
          <strong>How to get your session cookie:</strong><br/>
          1. Open <strong>infra.bewan.be</strong> → log in via Azure AD<br/>
          2. Press <strong>F12</strong> → Application → Cookies → <code style={{ background: 'rgba(0,0,0,.07)', padding: '1px 5px', borderRadius: 4 }}>https://infra.bewan.be</code><br/>
          3. Find <strong>JSESSIONID</strong> → copy the value → paste below
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <input type="password" placeholder="Paste JSESSIONID value here…" value={cookie} onChange={e => setCookie(e.target.value)}
            className="input mono" style={{ fontSize: 12 }} />
          <button className="btn btn-primary" onClick={saveCookie} disabled={cookieSaving || !cookie}
            style={{ opacity: !cookie ? .5 : 1, whiteSpace: 'nowrap', flexShrink: 0 }}>
            {cookieSaving ? 'Saving…' : '🔒 Save encrypted'}
          </button>
        </div>
        <div style={{ fontSize: 11, color: 'var(--text-2)', marginTop: 6 }}>Cookie encrypted with AES-128 · Never stored in plain text</div>
      </Section>

      {/* ── Bookmarklet ── */}
      <Section title="🔖 Bookmarklet — Auto-renew Session">
        <p style={{ fontSize: 12, color: 'var(--text-2)', marginBottom: 14, lineHeight: 1.7 }}>
          Once set up, clicking this bookmarklet while logged into Jira pushes a fresh session to the dashboard automatically — no manual cookie copy needed.
        </p>
        <button className="btn btn-primary" onClick={generateBookmarklet} disabled={bmLoading || !adminUser || !adminPass}
          style={{ opacity: (bmLoading || !adminUser || !adminPass) ? .5 : 1 }}>
          {bmLoading ? 'Generating…' : 'Generate Bookmarklet Token'}
        </button>
        {bmToken && (() => {
          const dashUrl = window.location.origin;
          const code = `javascript:(function(){var m=document.cookie.match(/JSESSIONID=([^;]+)/);if(!m){alert('No Jira session found. Log in to Jira first.');return;}fetch('${dashUrl}/api/jira/bookmarklet-renew',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({token:'${bmToken}',jsessionid:m[1]})}).then(r=>r.json()).then(d=>{if(d.ok)alert('✓ Session refreshed!');else alert('✗ '+(d.detail||'error'));}).catch(()=>alert('✗ Could not reach dashboard.'));})();`;
          return (
            <div style={{ marginTop: 16, padding: '12px 14px', background: 'var(--ok-light)', borderRadius: 8, border: '1px solid var(--ok)' }}>
              <div style={{ fontSize: 12, color: 'var(--ok)', fontWeight: 600, marginBottom: 8 }}>✓ Token generated — drag the button below to your bookmarks bar:</div>
              <a href={code} onClick={e => e.preventDefault()}
                style={{ display: 'inline-block', padding: '7px 14px', background: 'var(--accent)', color: '#fff', borderRadius: 6, fontWeight: 700, fontSize: 13, textDecoration: 'none' }}>
                🔑 Refresh Jira Session
              </a>
              <div style={{ fontSize: 11, color: 'var(--text-3)', marginTop: 8, lineHeight: 1.7 }}>
                Drag this button to your browser bookmarks bar. Then, when your Jira session expires, open Jira, click the bookmark, and you're done.
              </div>
              <div style={{ fontSize: 11, color: 'var(--warn)', marginTop: 6 }}>
                ⚠ Keep this token secret — anyone who has it can update the Jira session. Generating a new token invalidates the old one.
              </div>
            </div>
          );
        })()}
      </Section>

      {/* ── Sync ── */}
      <Section title="🔄 Data Sync">
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
          <div>
            <div style={{ fontSize: 13, fontWeight: 500 }}>Sync from Jira</div>
            <div style={{ fontSize: 12, color: 'var(--text-2)', marginTop: 2 }}>
              {uploads.filter(u => isSync(u.filename)).length > 0
                ? <>Last sync: <strong>{fmt(uploads.find(u => isSync(u.filename))?.uploaded_at)}</strong> · <strong style={{ color: 'var(--accent)' }}>{uploads.find(u => isSync(u.filename))?.ticket_count}</strong> tickets</>
                : 'No sync yet'}
            </div>
          </div>
          <button className="btn btn-primary" onClick={doSync} disabled={!jiraOk || syncing}
            style={{ opacity: (!jiraOk || syncing) ? .5 : 1 }}>
            {syncing
              ? <><svg width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" style={{ animation: 'spin 1s linear infinite' }}><path d="M21 12a9 9 0 1 1-6.219-8.56"/></svg>Syncing…</>
              : <><svg width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><polyline points="23 4 23 10 17 10"/><path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"/></svg>Sync from Jira</>
            }
            <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
          </button>
        </div>

        {/* Historical backfill */}
        <div style={{ borderTop: '1px solid var(--border)', paddingTop: 14, marginBottom: 14 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div>
              <div style={{ fontSize: 13, fontWeight: 500 }}>Historical backfill</div>
              <div style={{ fontSize: 12, color: 'var(--text-2)', marginTop: 2 }}>
                Imports resolved tickets from 1 Jan {new Date().getFullYear()} up to 13 weeks ago — runs in background
              </div>
            </div>
            <button className="btn btn-secondary" onClick={doBackfill} disabled={!jiraOk || backfilling}
              style={{ opacity: (!jiraOk || backfilling) ? .5 : 1, whiteSpace: 'nowrap' }}>
              {backfilling ? 'Backfilling…' : 'Backfill year'}
            </button>
          </div>
          {backfillMsg && (
            <div style={{ marginTop: 10, padding: '8px 12px', borderRadius: 6, fontSize: 12,
              background: backfillMsg.type === 'ok' ? 'var(--ok-light)' : backfillMsg.type === 'error' ? 'var(--critical-light)' : 'var(--accent-light)',
              color: backfillMsg.type === 'ok' ? 'var(--ok)' : backfillMsg.type === 'error' ? 'var(--critical)' : 'var(--accent)' }}>
              {backfillMsg.text}
            </div>
          )}
        </div>

        {/* CSV Fallback */}
        <div style={{ borderTop: '1px solid var(--border)', paddingTop: 14 }}>
          <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 8 }}>CSV Fallback <span style={{ fontWeight: 400, color: 'var(--text-2)' }}>— use when Jira is unreachable</span></div>
          <div
            className={`upload-zone ${dragover ? 'dragover' : ''}`}
            onDragOver={e => { e.preventDefault(); setDragover(true); }}
            onDragLeave={() => setDragover(false)}
            onDrop={e => { e.preventDefault(); setDragover(false); doUpload(e.dataTransfer.files[0]); }}
            onClick={() => fileRef.current.click()}
            style={{ padding: '20px 24px' }}>
            <svg width="28" height="28" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24" style={{ marginBottom: 6 }}>
              <polyline points="16 16 12 12 8 16"/><line x1="12" y1="12" x2="12" y2="21"/>
              <path d="M20.39 18.39A5 5 0 0 0 18 9h-1.26A8 8 0 1 0 3 16.3"/>
            </svg>
            <p style={{ fontSize: 13 }}><strong>Click to browse</strong> or drag & drop your CSV</p>
          </div>
          <input ref={fileRef} type="file" accept=".csv" style={{ display: 'none' }} onChange={e => doUpload(e.target.files[0])} />
          {uploading && <div style={{ marginTop: 10, fontSize: 13, color: 'var(--accent)' }}>Uploading and parsing…</div>}
        </div>
      </Section>

      {/* Azure AD SSO */}
      <Section icon="🔐" title="Azure AD SSO Login" badge={sso.enabled ? { text: '✓ Configured', color: 'var(--ok)' } : { text: 'Not configured', color: 'var(--text-2)' }}>
        <p style={{ fontSize: 13, color: 'var(--text-2)', marginBottom: 16 }}>
          Allow team members to sign in with their Microsoft account. Requires an Azure AD app registration with redirect URI: <code style={{ background: 'var(--surface-2)', padding: '1px 6px', borderRadius: 4, fontSize: 12 }}>{window.location.origin}/api/auth/sso/callback</code>
        </p>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
          <div>
            <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-2)', display: 'block', marginBottom: 4 }}>Directory (Tenant) ID</label>
            <input value={sso.tenant_id} onChange={e => setSso(s => ({...s, tenant_id: e.target.value}))}
              placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
              className="input" />
          </div>
          <div>
            <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-2)', display: 'block', marginBottom: 4 }}>Application (Client) ID</label>
            <input value={sso.client_id} onChange={e => setSso(s => ({...s, client_id: e.target.value}))}
              placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
              className="input" />
          </div>
          <div style={{ gridColumn: '1 / -1' }}>
            <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-2)', display: 'block', marginBottom: 4 }}>Client Secret Value</label>
            <input type="password" value={sso.client_secret} onChange={e => setSso(s => ({...s, client_secret: e.target.value}))}
              placeholder="Paste new secret value (leave empty to keep current)"
              className="input" />
          </div>
          <div style={{ gridColumn: '1 / -1' }}>
            <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-2)', display: 'block', marginBottom: 4 }}>
              Allowed Users
              <span style={{ fontWeight: 400, marginLeft: 6 }}>— leave empty to allow all users in the tenant</span>
            </label>
            <textarea
              value={sso.allowed_emails}
              onChange={e => setSso(s => ({...s, allowed_emails: e.target.value}))}
              placeholder={"dimitri.de.backer@bewan.be\nother.user@bewan.be"}
              className="input"
              rows={4}
              style={{ fontFamily: 'DM Mono, monospace', fontSize: 12, resize: 'vertical' }}
            />
            <div style={{ fontSize: 11, color: 'var(--text-2)', marginTop: 4 }}>
              One M365 email per line (or comma-separated). Only listed users can log in.
            </div>
          </div>
        </div>
        {ssoMsg && <div style={{ marginBottom: 10, fontSize: 13, color: ssoMsg.includes('✓') ? 'var(--ok)' : 'var(--critical)' }}>{ssoMsg}</div>}
        <button className="btn btn-primary" disabled={ssoSaving || !adminUser || !adminPass}
          onClick={async () => {
            setSsoSaving(true); setSsoMsg('');
            try {
              const r = await fetch(`${API}/api/settings/sso`, {
                method: 'POST', headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                  admin_user: adminUser, admin_pass: adminPass,
                  ...sso,
                  allowed_emails: sso.allowed_emails.split(/[\n,]/).map(e => e.trim()).filter(Boolean).join(','),
                }),
              });
              const d = await r.json();
              if (!r.ok) throw new Error(d.detail);
              setSsoMsg('✓ SSO settings saved');
              fetch(`${API}/api/settings/sso`).then(r => r.json()).then(d => setSso(d));
            } catch(e) { setSsoMsg('Error: ' + e.message); }
            setSsoSaving(false);
          }}>
          {ssoSaving ? 'Saving…' : 'Save SSO Settings'}
        </button>
      </Section>

    </div>
  );
}
